<template>
    <div 
        class="notification-item"
        :class="{ 
            'notification-exiting': isExiting,
            'notification-clickable': isClickable
        }"
        @click="handleClick"
    >
        <button 
            v-if="!isWorking"
            class="notification-close"
            @click.stop="handleClose"
            aria-label="Close notification"
        >
            <img src="/img/icons/avid-icons/close.svg" alt="Close" class="icon">
        </button>
        <div class="notification-content" :class="{ 'notification-with-progress': hasProgress }">
            <div 
                class="notification-icon"
                :class="{ 'notification-icon-danger': isDangerIcon }"
            >
                <img :src="notification.icon" :alt="notification.text" class="icon">
            </div>
            <div class="notification-text">
                <span v-html="formattedText"></span>
                <div v-if="hasProgress" class="notification-progress-container">
                    <div class="notification-progress-bar" :style="{ width: `${progressPercentage}%` }"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
    notification: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['close', 'click']);

const isExiting = ref(false);

/**
 * Check if the icon is the danger icon
 */
const isDangerIcon = computed(() => {
    return props.notification.icon && props.notification.icon.includes('danger.svg');
});

/**
 * Check if notification has progress bar (only show when working, not completed)
 */
const hasProgress = computed(() => {
    return props.notification.percentage !== undefined && 
           props.notification.percentage !== null && 
           props.notification.status === 'working';
});

/**
 * Get progress percentage (0-100)
 */
const progressPercentage = computed(() => {
    if (!hasProgress.value) return 0;
    return Math.min(100, Math.max(0, props.notification.percentage * 100));
});

/**
 * Check if notification is in working state (not dismissible)
 */
const isWorking = computed(() => {
    return props.notification.status === 'working';
});

/**
 * Check if notification is clickable (has link, external link, or action)
 * Exclude reEnableSuggestion actions as they're handled automatically
 */
const isClickable = computed(() => {
    const hasAction = props.notification.action && props.notification.action.type !== 'reEnableSuggestion';
    return !!(props.notification.link || props.notification.externalLink || hasAction);
});

/**
 * Format text to handle external links and actions (blue underlined)
 */
const formattedText = computed(() => {
    let text = props.notification.text;
    
    // If there's an action or external link, style the text as a link
    // Exclude reEnableSuggestion actions as they're handled automatically and shouldn't look clickable
    const hasAction = props.notification.action && props.notification.action.type !== 'reEnableSuggestion';
    if (hasAction || props.notification.externalLink) {
        // If there's an external link, wrap the link text in a span with blue underline
        if (props.notification.externalLink) {
            // Find text that should be linked (could be the entire text or a portion)
            // For now, we'll check if the text contains the link text pattern
            const linkText = extractLinkText(text);
            if (linkText) {
                text = text.replace(
                    linkText,
                    `<span class="notification-external-link">${linkText}</span>`
                );
            }
            //If it isn't a link in the text, wrap the whole thing
            else {
                text = `<span class="notification-external-link">${text}</span>`
            }
        } else if (hasAction) {
            // Style as link for actions (like opening modals)
            text = `<span class="notification-external-link">${text}</span>`;
        }
    }
    
    return text;
});

/**
 * Extract link text from notification text
 * This assumes the link text might be in quotes or marked somehow
 * Adjust based on your actual data format
 */
function extractLinkText(text) {
    // Simple pattern: Extract the text inside an <a>...</a> tag, if present
    const match = text.match(/<a [^>]*>(.*?)<\/a>/i);
    if (match) {
        return match[1];
    }
    return null;
}

function handleClick() {
    console.log('NotificationItem: Clicked, notification:', props.notification);
    emit('click', props.notification);
}

function handleClose() {
    isExiting.value = true;
    // Wait for animation to complete
    setTimeout(() => {
        emit('close', props.notification.id);
    }, 300);
}
</script>

